import React, {useCallback, useState} from 'react';
import I18n from 'app/i18n';
import {inject, observer} from 'mobx-react';
import {ModalsStore} from 'app/store/ModalsStore';
import {type CountryInterface} from 'app/interfaces/CountryInterface';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {PopupDialog} from 'app/utils/modals/PopupDialog';
import {showPopup} from 'app/utils/modals/ModalsService';
import {configStore} from 'app/store/global';

interface RestrictedCountriesPopupProps {
    popupId: string;
    gameName: string;
    restrictedCountryCodes: string[];
    modalsStore?: ModalsStore;
}

export const showRestrictedCountriesPopup = (
    gameName: string,
    restrictedCountryCodes: string[] | undefined
): void => {
    if (!restrictedCountryCodes?.length || !gameName.length) {
        return;
    }

    showPopup('popup-casino-restricted-countries', props => <RestrictedCountriesPopup
        gameName={gameName}
        popupId={props.popupId}
        restrictedCountryCodes={restrictedCountryCodes}
    />);
};

const RestrictedCountriesPopup = inject('modalsStore')(observer(
    ({popupId, gameName, restrictedCountryCodes, modalsStore}: RestrictedCountriesPopupProps) => {
        const [search, setSearch] = useState('');

        const allCountries: CountryInterface[] = configStore.ALL_COUNTRIES || [];

        const restrictedSet = new Set(restrictedCountryCodes.map(c => c.toLowerCase()));

        const filteredCountries = search.trim()
            ? allCountries.filter(c => c.title.toLowerCase().includes(search.toLowerCase()))
            : allCountries;

        const availableCountries = filteredCountries.filter(c => !restrictedSet.has(c.country_code.toLowerCase()));
        const unavailableCountries = filteredCountries.filter(c => restrictedSet.has(c.country_code.toLowerCase()));

        const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
            setSearch(e.target.value);
        }, []);

        const handleClose = useCallback((e?: React.MouseEvent<HTMLButtonElement>) => {
            e?.preventDefault();
            modalsStore.hideModal(popupId);
        }, [modalsStore, popupId]);

        return (
            <PopupDialog id={popupId} hideCloseButton={true} onClose={handleClose}>
                <div className="popup__content">
                    <div className="casino-regions-popup">
                        <button
                            type="button"
                            className="popup__close effect-no-tap casino-regions-popup__close"
                            onClick={handleClose}
                        >
                            <SpriteIcon name="#svg-close" size={{height: 16, width: 16}}/>
                        </button>
                        <h3 className="casino-regions-popup__title">
                            {I18n.t('casino_available_regions')}
                        </h3>
                        <p className="casino-regions-popup__subtitle">
                            {`${gameName} ${I18n.t('casino_game_unavailable_in_region')}`}
                        </p>
                        <div className="casino-regions-popup__search">
                            <SpriteIcon name="#svg-search-icon" className="svg-default casino-regions-popup__search-icon"/>
                            <input
                                className="casino-regions-popup__search-input"
                                id="casino-regions-popup-search"
                                name="casino-regions-popup-search"
                                type="text"
                                placeholder={I18n.t('casino_search_countries')}
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <div className="casino-regions-popup__headers">
                            <div className="casino-regions-popup__column-header casino-regions-popup__column-header--available">
                                <SpriteIcon
                                    name="#svg-globe"
                                    className="svg-default casino-regions-popup__globe-icon casino-regions-popup__globe-icon--available"
                                />
                                <span>{I18n.t('casino_available')}</span>
                            </div>
                            <div className="casino-regions-popup__column-header casino-regions-popup__column-header--unavailable">
                                <SpriteIcon
                                    name="#svg-globe"
                                    className="svg-default casino-regions-popup__globe-icon casino-regions-popup__globe-icon--unavailable"
                                />
                                <span>{I18n.t('casino_unavailable')}</span>
                            </div>
                        </div>
                        <div className="casino-regions-popup__columns">
                            <div className="casino-regions-popup__column">
                                <ul className="casino-regions-popup__list">
                                    {availableCountries.map((country: CountryInterface) => <li
                                        key={country.country_code}
                                        className="casino-regions-popup__list-item"
                                    >
                                        {country.title}
                                    </li>)}
                                </ul>
                            </div>
                            <div className="casino-regions-popup__column">
                                <ul className="casino-regions-popup__list">
                                    {unavailableCountries.map((country: CountryInterface) => <li
                                        key={country.country_code}
                                        className="casino-regions-popup__list-item"
                                    >
                                        {country.title}
                                    </li>)}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </PopupDialog>
        );
    }
));

export default RestrictedCountriesPopup;
